/**
 * InterventionScheduler - Interface de planification complexe multi-intervenants
 *
 * Ce composant permet de :
 * - Configurer des jours fixes par intervenant
 * - Configurer une alternance semaine A/B
 * - Détecter automatiquement les majorations (dimanche, jours fériés)
 * - Valider la cohérence du planning
 * - Prévisualiser le planning et les statistiques
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, Plus, Trash2, AlertTriangle, CheckCircle, Calendar, Clock } from 'lucide-react';
import {
  validateSchedule,
  generateInterventionSchedule,
  calculateTotalInterventions
} from '../services/complexSchedulingService';
import './InterventionScheduler.css';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Lundi', short: 'Lun' },
  { value: 2, label: 'Mardi', short: 'Mar' },
  { value: 3, label: 'Mercredi', short: 'Mer' },
  { value: 4, label: 'Jeudi', short: 'Jeu' },
  { value: 5, label: 'Vendredi', short: 'Ven' },
  { value: 6, label: 'Samedi', short: 'Sam' },
  { value: 0, label: 'Dimanche', short: 'Dim', isSunday: true }
];

const InterventionScheduler = ({
  employees = [],
  periodePrestation,
  weeklyHoursTarget,
  onSave,
  onCancel,
  initialSchedules = []
}) => {
  const [employeeSchedules, setEmployeeSchedules] = useState(
    initialSchedules.length > 0
      ? initialSchedules
      : [{
          id: Date.now(),
          employeeId: null,
          employeeName: '',
          fixedDays: [],
          alternatingPattern: {
            enabled: false,
            weekA: [],
            weekB: []
          },
          distanceAllerKm: 0
        }]
  );

  const [validation, setValidation] = useState({ valid: true, errors: [], warnings: [] });
  const [previewData, setPreviewData] = useState(null);

  // Validation en temps réel
  useEffect(() => {
    const result = validateSchedule(employeeSchedules, weeklyHoursTarget);
    setValidation(result);

    // Générer l'aperçu si planning valide et période définie
    if (result.valid && periodePrestation?.dateDebut && periodePrestation?.dateFin) {
      try {
        const schedule = generateInterventionSchedule(
          periodePrestation.dateDebut,
          periodePrestation.dateFin,
          employeeSchedules.filter(es => es.employeeId)
        );
        const stats = calculateTotalInterventions(schedule);
        setPreviewData(stats);
      } catch (error) {
        console.error('Erreur génération aperçu:', error);
        setPreviewData(null);
      }
    } else {
      setPreviewData(null);
    }
  }, [employeeSchedules, weeklyHoursTarget, periodePrestation]);

  // Ajouter un nouvel intervenant
  const handleAddEmployee = () => {
    setEmployeeSchedules([
      ...employeeSchedules,
      {
        id: Date.now(),
        employeeId: null,
        employeeName: '',
        fixedDays: [],
        alternatingPattern: {
          enabled: false,
          weekA: [],
          weekB: []
        },
        distanceAllerKm: 0
      }
    ]);
  };

  // Supprimer un intervenant
  const handleRemoveEmployee = (scheduleId) => {
    if (employeeSchedules.length > 1) {
      setEmployeeSchedules(employeeSchedules.filter(es => es.id !== scheduleId));
    }
  };

  // Sélectionner un employé
  const handleSelectEmployee = (scheduleId, employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    setEmployeeSchedules(employeeSchedules.map(es =>
      es.id === scheduleId
        ? {
            ...es,
            employeeId,
            employeeName: employee ? `${employee.firstName} ${employee.lastName}` : ''
          }
        : es
    ));
  };

  // Toggle jour fixe
  const handleToggleFixedDay = (scheduleId, day) => {
    setEmployeeSchedules(employeeSchedules.map(es => {
      if (es.id !== scheduleId) return es;

      const exists = es.fixedDays.find(fd => fd.day === day);
      return {
        ...es,
        fixedDays: exists
          ? es.fixedDays.filter(fd => fd.day !== day)
          : [...es.fixedDays, {
              day,
              totalHours: 1,
              totalMinutes: 0,
              passagesPerDay: 1
            }]
      };
    }));
  };

  // Mettre à jour heures d'un jour fixe
  const handleUpdateFixedDayTime = (scheduleId, day, field, value) => {
    setEmployeeSchedules(employeeSchedules.map(es => {
      if (es.id !== scheduleId) return es;

      return {
        ...es,
        fixedDays: es.fixedDays.map(fd =>
          fd.day === day ? { ...fd, [field]: parseInt(value) || 0 } : fd
        )
      };
    }));
  };

  // Toggle alternance
  const handleToggleAlternance = (scheduleId) => {
    setEmployeeSchedules(employeeSchedules.map(es =>
      es.id === scheduleId
        ? {
            ...es,
            alternatingPattern: {
              ...es.alternatingPattern,
              enabled: !es.alternatingPattern.enabled
            }
          }
        : es
    ));
  };

  // Toggle jour alternant
  const handleToggleAlternatingDay = (scheduleId, weekType, day) => {
    setEmployeeSchedules(employeeSchedules.map(es => {
      if (es.id !== scheduleId) return es;

      const weekDays = es.alternatingPattern[weekType] || [];
      const exists = weekDays.find(wd => wd.day === day);

      return {
        ...es,
        alternatingPattern: {
          ...es.alternatingPattern,
          [weekType]: exists
            ? weekDays.filter(wd => wd.day !== day)
            : [...weekDays, {
                day,
                totalHours: 1,
                totalMinutes: 0,
                passagesPerDay: 1
              }]
        }
      };
    }));
  };

  // Mettre à jour temps d'un jour alternant
  const handleUpdateAlternatingDayTime = (scheduleId, weekType, day, field, value) => {
    setEmployeeSchedules(employeeSchedules.map(es => {
      if (es.id !== scheduleId) return es;

      return {
        ...es,
        alternatingPattern: {
          ...es.alternatingPattern,
          [weekType]: es.alternatingPattern[weekType].map(wd =>
            wd.day === day ? { ...wd, [field]: parseInt(value) || 0 } : wd
          )
        }
      };
    }));
  };

  // Mettre à jour distance
  const handleUpdateDistance = (scheduleId, distance) => {
    setEmployeeSchedules(employeeSchedules.map(es =>
      es.id === scheduleId
        ? { ...es, distanceAllerKm: parseFloat(distance) || 0 }
        : es
    ));
  };

  // Sauvegarder
  const handleSave = () => {
    if (validation.valid) {
      onSave(employeeSchedules, previewData);
    }
  };

  // Vérifier si un dimanche est sélectionné
  const hasSundaySelected = useMemo(() => {
    return employeeSchedules.some(es =>
      es.fixedDays.some(fd => fd.day === 0) ||
      (es.alternatingPattern.weekA || []).some(wd => wd.day === 0) ||
      (es.alternatingPattern.weekB || []).some(wd => wd.day === 0)
    );
  }, [employeeSchedules]);

  return (
    <div className="scheduler-modal-overlay">
      <div className="scheduler-modal">
        {/* Header */}
        <div className="scheduler-header">
          <div>
            <h2>Planification des interventions</h2>
            <p>Configurez les jours et heures d'intervention pour chaque intervenant</p>
          </div>
          <button onClick={onCancel} className="scheduler-close-btn">
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="scheduler-body">
          {/* Configuration globale */}
          <div className="scheduler-section">
            <h3 className="scheduler-section-title">
              <Calendar size={20} />
              Configuration globale
            </h3>
            <div className="scheduler-global-config">
              <div className="scheduler-info-item">
                <span className="scheduler-info-label">Période</span>
                <span className="scheduler-info-value">
                  {periodePrestation?.dateDebut && periodePrestation?.dateFin
                    ? `${periodePrestation.dateDebut.toLocaleDateString('fr-FR')} - ${periodePrestation.dateFin.toLocaleDateString('fr-FR')}`
                    : 'Non définie'}
                </span>
              </div>
              <div className="scheduler-info-item">
                <span className="scheduler-info-label">Volume contractuel</span>
                <span className="scheduler-info-value">{weeklyHoursTarget}h / semaine</span>
              </div>
              <div className="scheduler-info-item">
                <span className="scheduler-info-label">Volume configuré (moyenne)</span>
                <span className="scheduler-info-value" style={{
                  color: validation.totalWeeklyHours === weeklyHoursTarget ? '#15803d' : '#ea580c'
                }}>
                  {validation.totalWeeklyHours || 0}h / semaine
                </span>
                {validation.weeklyHoursDetail && (
                  <span style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    {validation.weeklyHoursDetail}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Validation */}
          {(validation.errors.length > 0 || validation.warnings.length > 0) && (
            <div className="scheduler-section">
              {validation.errors.length > 0 && (
                <div className="scheduler-validation-alert error">
                  <AlertTriangle size={20} />
                  <div>
                    <strong>Erreurs détectées :</strong>
                    <ul className="scheduler-validation-list">
                      {validation.errors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              {validation.warnings.length > 0 && (
                <div className="scheduler-validation-alert warning">
                  <AlertTriangle size={20} />
                  <div>
                    <strong>Avertissements :</strong>
                    <ul className="scheduler-validation-list">
                      {validation.warnings.map((warning, idx) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Avertissement dimanche */}
          {hasSundaySelected && (
            <div className="scheduler-sunday-warning">
              <AlertTriangle size={16} />
              <span>Les interventions du dimanche seront automatiquement majorées de +25%</span>
            </div>
          )}

          {/* Configuration par intervenant */}
          <div className="scheduler-section">
            <h3 className="scheduler-section-title">
              <Clock size={20} />
              Configuration par intervenant
            </h3>

            {employeeSchedules.map((schedule) => (
              <EmployeeScheduleCard
                key={schedule.id}
                schedule={schedule}
                employees={employees}
                onSelectEmployee={handleSelectEmployee}
                onRemove={handleRemoveEmployee}
                onToggleFixedDay={handleToggleFixedDay}
                onUpdateFixedDayTime={handleUpdateFixedDayTime}
                onToggleAlternance={handleToggleAlternance}
                onToggleAlternatingDay={handleToggleAlternatingDay}
                onUpdateAlternatingDayTime={handleUpdateAlternatingDayTime}
                onUpdateDistance={handleUpdateDistance}
                canRemove={employeeSchedules.length > 1}
              />
            ))}

            <button onClick={handleAddEmployee} className="scheduler-add-employee-btn">
              <Plus size={16} />
              Ajouter un intervenant
            </button>
          </div>

          {/* Aperçu */}
          {previewData && Object.keys(previewData).length > 0 && (
            <div className="scheduler-section">
              <div className="scheduler-summary">
                <h3 className="scheduler-summary-title">Récapitulatif du planning</h3>
                <table className="scheduler-summary-table">
                  <thead>
                    <tr>
                      <th>Intervenant</th>
                      <th>Heures normales</th>
                      <th>Heures dimanche</th>
                      <th>Heures fériés</th>
                      <th>Heures 1er mai</th>
                      <th>Total heures</th>
                      <th>Total passages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(previewData).map((stats) => (
                      <tr key={stats.employeeId}>
                        <td>{stats.employeeName}</td>
                        <td>
                          {stats.none.hours.toFixed(1)}h
                          <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                            {stats.none.interventions} passage{stats.none.interventions > 1 ? 's' : ''}
                          </div>
                        </td>
                        <td>
                          {stats.weekend.hours.toFixed(1)}h {stats.weekend.hours > 0 && <span style={{color: '#f59e0b'}}>+25%</span>}
                          {stats.weekend.interventions > 0 && (
                            <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                              {stats.weekend.interventions} passage{stats.weekend.interventions > 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          {stats.holiday.hours.toFixed(1)}h {stats.holiday.hours > 0 && <span style={{color: '#f97316'}}>+50%</span>}
                          {stats.holiday.interventions > 0 && (
                            <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                              {stats.holiday.interventions} passage{stats.holiday.interventions > 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          {stats.may1.hours.toFixed(1)}h {stats.may1.hours > 0 && <span style={{color: '#dc2626'}}>+100%</span>}
                          {stats.may1.interventions > 0 && (
                            <div style={{fontSize: '0.75rem', color: '#6b7280'}}>
                              {stats.may1.interventions} passage{stats.may1.interventions > 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        <td><strong>{stats.total.hours.toFixed(1)}h</strong></td>
                        <td><strong>{stats.total.interventions} passage{stats.total.interventions > 1 ? 's' : ''}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="scheduler-footer">
          <button onClick={onCancel} className="scheduler-footer-btn secondary">
            Annuler
          </button>
          <button
            onClick={handleSave}
            className="scheduler-footer-btn primary"
            disabled={!validation.valid}
          >
            <CheckCircle size={16} />
            Valider le planning
          </button>
        </div>
      </div>
    </div>
  );
};

// Sous-composant pour une carte d'employé
const EmployeeScheduleCard = ({
  schedule,
  employees,
  onSelectEmployee,
  onRemove,
  onToggleFixedDay,
  onUpdateFixedDayTime,
  onToggleAlternance,
  onToggleAlternatingDay,
  onUpdateAlternatingDayTime,
  onUpdateDistance,
  canRemove
}) => {
  return (
    <div className="scheduler-employee-card">
      {/* Header */}
      <div className="scheduler-employee-header">
        <select
          className="scheduler-employee-select"
          value={schedule.employeeId || ''}
          onChange={(e) => onSelectEmployee(schedule.id, e.target.value)}
        >
          <option value="">Sélectionner un intervenant...</option>
          {employees.map((emp) => (
            <option key={emp.id} value={emp.id}>
              {emp.firstName} {emp.lastName}
            </option>
          ))}
        </select>
        {canRemove && (
          <button
            onClick={() => onRemove(schedule.id)}
            className="scheduler-remove-btn"
          >
            <Trash2 size={16} />
            Supprimer
          </button>
        )}
      </div>

      {/* Jours fixes */}
      <div>
        <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>
          Jours fixes (toutes les semaines)
        </h4>
        <div className="scheduler-days-grid">
          {DAYS_OF_WEEK.map((day) => {
            const isSelected = schedule.fixedDays.some(fd => fd.day === day.value);
            const dayData = schedule.fixedDays.find(fd => fd.day === day.value);

            // Calculer la durée par passage
            const hoursPerPassage = dayData ?
              ((dayData.totalHours || 0) + (dayData.totalMinutes || 0) / 60) / (dayData.passagesPerDay || 1) : 0;
            const hrsPerPass = Math.floor(hoursPerPassage);
            const minPerPass = Math.round((hoursPerPassage - hrsPerPass) * 60);

            return (
              <div
                key={day.value}
                className={`scheduler-day-checkbox ${isSelected ? 'selected' : ''} ${day.isSunday ? 'is-sunday' : ''}`}
                style={{ flexDirection: 'column', alignItems: 'flex-start', padding: isSelected ? '0.75rem' : '0.75rem' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: isSelected ? '0.5rem' : '0' }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleFixedDay(schedule.id, day.value)}
                  />
                  <span className="scheduler-day-label">{day.label}</span>
                </div>
                {isSelected && (
                  <div style={{ width: '100%', fontSize: '0.7rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    <div style={{ display: 'flex', gap: '0.2rem', alignItems: 'center' }}>
                      <input
                        type="number"
                        className="scheduler-hours-input"
                        value={dayData?.totalHours || 1}
                        onChange={(e) => {
                          const val = Math.max(0, Math.min(23, parseInt(e.target.value) || 0));
                          onUpdateFixedDayTime(schedule.id, day.value, 'totalHours', val);
                        }}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            const val = Math.min(23, (dayData?.totalHours || 1) + 1);
                            onUpdateFixedDayTime(schedule.id, day.value, 'totalHours', val);
                          } else if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const val = Math.max(0, (dayData?.totalHours || 1) - 1);
                            onUpdateFixedDayTime(schedule.id, day.value, 'totalHours', val);
                          }
                        }}
                        min="0"
                        max="23"
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '1.8rem', padding: '0.1rem', fontSize: '0.7rem', textAlign: 'center' }}
                      />
                      <span style={{ fontSize: '0.65rem' }}>h</span>
                      <input
                        type="number"
                        className="scheduler-hours-input"
                        value={dayData?.totalMinutes || 0}
                        onChange={(e) => {
                          const val = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                          onUpdateFixedDayTime(schedule.id, day.value, 'totalMinutes', val);
                        }}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            const val = Math.min(59, (dayData?.totalMinutes || 0) + 15);
                            onUpdateFixedDayTime(schedule.id, day.value, 'totalMinutes', val);
                          } else if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const val = Math.max(0, (dayData?.totalMinutes || 0) - 15);
                            onUpdateFixedDayTime(schedule.id, day.value, 'totalMinutes', val);
                          }
                        }}
                        min="0"
                        max="59"
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '1.8rem', padding: '0.1rem', fontSize: '0.7rem', textAlign: 'center' }}
                      />
                      <span style={{ fontSize: '0.65rem' }}>m</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.2rem', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.65rem', color: '#6b7280' }}>×</span>
                      <input
                        type="number"
                        className="scheduler-hours-input"
                        value={dayData?.passagesPerDay || 1}
                        onChange={(e) => {
                          const val = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                          onUpdateFixedDayTime(schedule.id, day.value, 'passagesPerDay', val);
                        }}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            const val = Math.min(10, (dayData?.passagesPerDay || 1) + 1);
                            onUpdateFixedDayTime(schedule.id, day.value, 'passagesPerDay', val);
                          } else if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const val = Math.max(1, (dayData?.passagesPerDay || 1) - 1);
                            onUpdateFixedDayTime(schedule.id, day.value, 'passagesPerDay', val);
                          }
                        }}
                        min="1"
                        max="10"
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '1.8rem', padding: '0.1rem', fontSize: '0.7rem', textAlign: 'center' }}
                      />
                      <span style={{ fontSize: '0.65rem', color: '#6b7280' }}>pass</span>
                    </div>
                    <div style={{ fontSize: '0.6rem', color: '#059669', fontWeight: '500', paddingTop: '0.1rem', borderTop: '1px solid #e5e7eb' }}>
                      {hrsPerPass}h{minPerPass > 0 ? String(minPerPass).padStart(2, '0') : '00'}/p
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Alternance */}
      <div className="scheduler-alternance-section">
        <div className="scheduler-alternance-toggle">
          <input
            type="checkbox"
            id={`alternance-${schedule.id}`}
            checked={schedule.alternatingPattern.enabled}
            onChange={() => onToggleAlternance(schedule.id)}
          />
          <label htmlFor={`alternance-${schedule.id}`}>
            Activer l'alternance semaine A/B
          </label>
        </div>

        {schedule.alternatingPattern.enabled && (
          <div className="scheduler-week-panels">
            {/* Semaine A */}
            <div className="scheduler-week-panel week-a">
              <div className="scheduler-week-title">
                <span className="scheduler-week-badge week-a">A</span>
                Semaine A (impaire)
              </div>
              <div className="scheduler-days-grid">
                {DAYS_OF_WEEK.map((day) => {
                  const isSelected = (schedule.alternatingPattern.weekA || []).some(wd => wd.day === day.value);
                  const dayData = (schedule.alternatingPattern.weekA || []).find(wd => wd.day === day.value);

                  // Calculer la durée par passage
                  const hoursPerPassage = dayData ?
                    ((dayData.totalHours || 0) + (dayData.totalMinutes || 0) / 60) / (dayData.passagesPerDay || 1) : 0;
                  const hrsPerPass = Math.floor(hoursPerPassage);
                  const minPerPass = Math.round((hoursPerPassage - hrsPerPass) * 60);

                  return (
                    <div
                      key={day.value}
                      className={`scheduler-day-checkbox ${isSelected ? 'selected' : ''} ${day.isSunday ? 'is-sunday' : ''}`}
                      style={{ gridColumn: 'span 1', flexDirection: 'column', alignItems: 'flex-start', padding: isSelected ? '0.5rem' : '0.5rem' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: isSelected ? '0.25rem' : '0' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleAlternatingDay(schedule.id, 'weekA', day.value)}
                        />
                        <span className="scheduler-day-label" style={{ fontSize: '0.75rem' }}>{day.short}</span>
                      </div>
                      {isSelected && (
                        <div style={{ width: '100%', fontSize: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          <div style={{ display: 'flex', gap: '0.15rem', alignItems: 'center' }}>
                            <input
                              type="number"
                              className="scheduler-hours-input"
                              value={dayData?.totalHours || 1}
                              onChange={(e) => {
                                const val = Math.max(0, Math.min(23, parseInt(e.target.value) || 0));
                                onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'totalHours', val);
                              }}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const val = Math.min(23, (dayData?.totalHours || 1) + 1);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'totalHours', val);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const val = Math.max(0, (dayData?.totalHours || 1) - 1);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'totalHours', val);
                                }
                              }}
                              min="0"
                              max="23"
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '1.5rem', padding: '0.1rem', fontSize: '0.6rem', textAlign: 'center' }}
                            />
                            <span style={{ fontSize: '0.55rem' }}>h</span>
                            <input
                              type="number"
                              className="scheduler-hours-input"
                              value={dayData?.totalMinutes || 0}
                              onChange={(e) => {
                                const val = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                                onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'totalMinutes', val);
                              }}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const val = Math.min(59, (dayData?.totalMinutes || 0) + 15);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'totalMinutes', val);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const val = Math.max(0, (dayData?.totalMinutes || 0) - 15);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'totalMinutes', val);
                                }
                              }}
                              min="0"
                              max="59"
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '1.5rem', padding: '0.1rem', fontSize: '0.6rem', textAlign: 'center' }}
                            />
                            <span style={{ fontSize: '0.55rem' }}>m</span>
                          </div>
                          <div style={{ display: 'flex', gap: '0.15rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.55rem', color: '#6b7280' }}>×</span>
                            <input
                              type="number"
                              className="scheduler-hours-input"
                              value={dayData?.passagesPerDay || 1}
                              onChange={(e) => {
                                const val = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                                onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'passagesPerDay', val);
                              }}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const val = Math.min(10, (dayData?.passagesPerDay || 1) + 1);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'passagesPerDay', val);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const val = Math.max(1, (dayData?.passagesPerDay || 1) - 1);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekA', day.value, 'passagesPerDay', val);
                                }
                              }}
                              min="1"
                              max="10"
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '1.5rem', padding: '0.1rem', fontSize: '0.6rem', textAlign: 'center' }}
                            />
                            <span style={{ fontSize: '0.55rem', color: '#6b7280' }}>p</span>
                          </div>
                          <div style={{ fontSize: '0.5rem', color: '#059669', fontWeight: '500', paddingTop: '0.1rem', borderTop: '1px solid #dbeafe' }}>
                            {hrsPerPass}h{minPerPass > 0 ? String(minPerPass).padStart(2, '0') : '00'}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Semaine B */}
            <div className="scheduler-week-panel week-b">
              <div className="scheduler-week-title">
                <span className="scheduler-week-badge week-b">B</span>
                Semaine B (paire)
              </div>
              <div className="scheduler-days-grid">
                {DAYS_OF_WEEK.map((day) => {
                  const isSelected = (schedule.alternatingPattern.weekB || []).some(wd => wd.day === day.value);
                  const dayData = (schedule.alternatingPattern.weekB || []).find(wd => wd.day === day.value);

                  // Calculer la durée par passage pour Semaine B
                  const hoursPerPassage = dayData ?
                    ((dayData.totalHours || 0) + (dayData.totalMinutes || 0) / 60) / (dayData.passagesPerDay || 1) : 0;
                  const hrsPerPass = Math.floor(hoursPerPassage);
                  const minPerPass = Math.round((hoursPerPassage - hrsPerPass) * 60);

                  return (
                    <div
                      key={day.value}
                      className={`scheduler-day-checkbox ${isSelected ? 'selected' : ''} ${day.isSunday ? 'is-sunday' : ''}`}
                      style={{ flexDirection: 'column', alignItems: 'flex-start', padding: isSelected ? '0.5rem' : '0.75rem' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: isSelected ? '0.25rem' : '0' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleAlternatingDay(schedule.id, 'weekB', day.value)}
                        />
                        <span className="scheduler-day-label" style={{ fontSize: '0.8rem' }}>{day.short}</span>
                      </div>
                      {isSelected && (
                        <div style={{ width: '100%', fontSize: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                          {/* Heures et minutes */}
                          <div style={{ display: 'flex', gap: '0.15rem', alignItems: 'center' }}>
                            <input
                              type="number"
                              className="scheduler-hours-input"
                              value={dayData?.totalHours || 1}
                              onChange={(e) => {
                                const val = Math.max(0, Math.min(23, parseInt(e.target.value) || 0));
                                onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'totalHours', val);
                              }}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const val = Math.min(23, (dayData?.totalHours || 1) + 1);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'totalHours', val);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const val = Math.max(0, (dayData?.totalHours || 1) - 1);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'totalHours', val);
                                }
                              }}
                              min="0"
                              max="23"
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '1.5rem', padding: '0.1rem', fontSize: '0.6rem', textAlign: 'center' }}
                            />
                            <span style={{ fontSize: '0.55rem' }}>h</span>
                            <input
                              type="number"
                              className="scheduler-hours-input"
                              value={dayData?.totalMinutes || 0}
                              onChange={(e) => {
                                const val = Math.max(0, Math.min(59, parseInt(e.target.value) || 0));
                                onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'totalMinutes', val);
                              }}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const val = Math.min(59, (dayData?.totalMinutes || 0) + 15);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'totalMinutes', val);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const val = Math.max(0, (dayData?.totalMinutes || 0) - 15);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'totalMinutes', val);
                                }
                              }}
                              min="0"
                              max="59"
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '1.5rem', padding: '0.1rem', fontSize: '0.6rem', textAlign: 'center' }}
                            />
                            <span style={{ fontSize: '0.55rem' }}>m</span>
                          </div>
                          {/* Passages */}
                          <div style={{ display: 'flex', gap: '0.15rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.55rem', color: '#6b7280' }}>×</span>
                            <input
                              type="number"
                              className="scheduler-hours-input"
                              value={dayData?.passagesPerDay || 1}
                              onChange={(e) => {
                                const val = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                                onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'passagesPerDay', val);
                              }}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const val = Math.min(10, (dayData?.passagesPerDay || 1) + 1);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'passagesPerDay', val);
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const val = Math.max(1, (dayData?.passagesPerDay || 1) - 1);
                                  onUpdateAlternatingDayTime(schedule.id, 'weekB', day.value, 'passagesPerDay', val);
                                }
                              }}
                              min="1"
                              max="10"
                              onClick={(e) => e.stopPropagation()}
                              style={{ width: '1.5rem', padding: '0.1rem', fontSize: '0.6rem', textAlign: 'center' }}
                            />
                            <span style={{ fontSize: '0.55rem', color: '#6b7280' }}>p</span>
                          </div>
                          {/* Durée calculée par passage */}
                          <div style={{ fontSize: '0.5rem', color: '#059669', fontWeight: '500', paddingTop: '0.1rem', borderTop: '1px solid #f5f3ff' }}>
                            {hrsPerPass}h{minPerPass > 0 ? String(minPerPass).padStart(2, '0') : '00'}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Distance */}
      <div className="scheduler-distance-input">
        <label>Distance aller (km)</label>
        <input
          type="number"
          value={schedule.distanceAllerKm}
          onChange={(e) => onUpdateDistance(schedule.id, e.target.value)}
          min="0"
          step="1"
        />
      </div>
    </div>
  );
};

export default InterventionScheduler;
